/** 团队（BS-05 子账号）—— 员工名册 + 邀请码。
 *
 * 设计权威：docs/platform/merchant-console/商家控制台员工邀请与角色组方案.md v0.2。
 * 三条裁定（主人 2026-10-13）：
 *   ① 商家**不能建角色**，只能把「平台开放的可用角色」分配给员工；
 *   ② 邀请**扫码即入**，**不带默认角色** —— 加入后菜单为空，等管理员分配；
 *   ③ 结构变更（mc_staff / mc_staff_role / mc_staff_invite）已获授权。
 *
 * 后端接口一律**人级闸**（`_require_team_perm` in routers.py），不是店级 ——
 * 所以这里 `api()` 必须带门户 Bearer（`portal` 模式），接口密钥会话进不来。
 */
import { $ } from "../lib/dom.js";
import { escHtml } from "../lib/hints.js";
import { memberAvatarHtml } from "../lib/avatar.js";
import { api } from "../api.js";

let assignableCache = null;

/** 能不能改（分配角色 / 停启用 / 发邀请）—— 只读角色只能看名册。
 *
 * 与后端同源：`mc:team:member:write` / `mc:team:invite`。**前端隐藏不等于后端放行**，
 * 后端 `_require_team_perm` 才是权威；这里只是别把点了必 403 的按钮摆出来。
 */
function canWrite() {
  return !!(window.McAuth && window.McAuth.hasPerm && window.McAuth.hasPerm("mc:team:member:write"));
}

function canInvite() {
  return !!(window.McAuth && window.McAuth.hasPerm && window.McAuth.hasPerm("mc:team:invite"));
}

function fmtTime(v) {
  if (!v) return "—";
  return String(v).replace("T", " ").slice(0, 16);
}

/** 名册行下面那行小字：「什么时候、以什么方式进的店」。
 *
 * 两种来源的时间含义不同，**文案必须分开**，否则看的人以为都是扫码进来的：
 *   · `invite` —— 他扫码的那一刻就是申请时刻，等于 `joined_at`；
 *   · 其余（存量补齐/管理员手工加入）—— 没有扫码这回事，时间是他**被开通**的时刻。
 */
function joinedLabel(m) {
  const t = fmtTime(m.joined_at);
  if (t === "—") return "加入时间未知";
  return String(m.joined_via || "") === "invite" ? `扫码加入 ${t}` : `开通 ${t}`;
}

const STAFF_STATUS_LABEL = { active: "在职", suspended: "已停用" };
const INVITE_STATUS_LABEL = {
  pending: "待使用",
  accepted: "已使用",
  revoked: "已撤销",
  expired: "已过期",
};

async function loadAssignable() {
  if (assignableCache) return assignableCache;
  const { ok, data } = await api("GET", "/api/v1/merchant/team/roles", { merchant: true });
  if (ok && data) assignableCache = data.assignable || [];
  return assignableCache || [];
}

/** 角色多选：勾上的 = 该员工当前持有的可用角色（覆盖式提交）。
 * 无写权限时渲染成**只读勾选框**（disabled），让人看得见"他有哪些角色"，点不动。 */
function rolesPickerHtml(personId, heldIds, assignable) {
  const held = new Set((heldIds || []).map((x) => Number(x)));
  const ro = canWrite() ? "" : " disabled";
  return assignable
    .map((r) => {
      const checked = held.has(Number(r.id)) ? " checked" : "";
      return (
        `<label class="mc-team-role-opt">` +
        `<input type="checkbox" data-pid="${escHtml(personId)}" value="${Number(r.id)}"${checked}${ro} />` +
        `<span>${escHtml(r.name || r.code)}</span>` +
        `</label>`
      );
    })
    .join("");
}

function renderMembers(items, assignable, myPid) {
  const box = $("team-members");
  if (!box) return;
  if (!items.length) {
    box.innerHTML =
      '<p class="mc-desc">本店还没有员工。切到「邀请」生成一张邀请码，让员工扫码加入。</p>';
    return;
  }
  box.innerHTML = items
    .map((m) => {
      const pid = String(m.person_id || "");
      const self = myPid && pid === myPid;
      const st = String(m.status || "active");
      const label = STAFF_STATUS_LABEL[st] || st;
      const toggle =
        st === "active"
          ? `<button type="button" class="secondary js-team-suspend" data-pid="${escHtml(pid)}"${
              self || !canWrite() ? ' disabled title="不能停用自己"' : ""
            }>停用</button>`
          : `<button type="button" class="secondary js-team-resume" data-pid="${escHtml(pid)}"${
              canWrite() ? "" : " disabled"
            }>恢复</button>`;
      return (
        `<div class="mc-team-row" data-pid="${escHtml(pid)}">` +
        `<div class="mc-team-cell mc-team-name">` +
        memberAvatarHtml(m) +
        `<div class="mc-team-namecol">` +
        `<b>${escHtml(m.nickname || m.display_name || "（未命名）")}</b>` +
        `<span class="mc-desc"> ${escHtml(pid)}${self ? " · 我" : ""}</span>` +
        `<div class="mc-desc mc-team-joined">${escHtml(joinedLabel(m))}</div>` +
        `</div>` +
        `</div>` +
        `<div class="mc-team-cell"><span class="mcp-badge ${st === "active" ? "live" : "bad"}">${escHtml(label)}</span></div>` +
        `<div class="mc-team-cell mc-team-roles">${rolesPickerHtml(pid, m.role_ids, assignable)}</div>` +
        `<div class="mc-team-cell mc-team-ops">` +
        (canWrite()
          ? `<button type="button" class="primary js-team-save" data-pid="${escHtml(pid)}">保存角色</button>`
          : "") +
        toggle +
        `</div>` +
        `</div>`
      );
    })
    .join("");

  box.querySelectorAll(".js-team-save").forEach((btn) => {
    btn.addEventListener("click", () => saveRoles(btn));
  });
  box.querySelectorAll(".js-team-suspend").forEach((btn) => {
    btn.addEventListener("click", () => setStatus(btn, "suspend"));
  });
  box.querySelectorAll(".js-team-resume").forEach((btn) => {
    btn.addEventListener("click", () => setStatus(btn, "resume"));
  });
}

async function saveRoles(btn) {
  const pid = btn.getAttribute("data-pid") || "";
  const row = btn.closest(".mc-team-row");
  const roleIds = Array.from(row ? row.querySelectorAll("input[type=checkbox]:checked") : []).map(
    (i) => Number(i.value)
  );
  btn.disabled = true;
  const { ok, data } = await api(
    "POST",
    `/api/v1/merchant/team/members/${encodeURIComponent(pid)}/roles`,
    { body: { role_ids: roleIds } }
  );
  btn.disabled = false;
  if (!ok) {
    alert((data && (data.detail || data.message)) || "保存失败");
    return;
  }
  await loadTeamMembers();
}

async function setStatus(btn, kind) {
  const pid = btn.getAttribute("data-pid") || "";
  const word = kind === "suspend" ? "停用" : "恢复";
  if (!confirm(`确认${word}该员工？\nperson_id：${pid}`)) return;
  btn.disabled = true;
  const { ok, data } = await api(
    "POST",
    `/api/v1/merchant/team/members/${encodeURIComponent(pid)}/${kind}`,
    {}
  );
  btn.disabled = false;
  if (!ok) {
    alert((data && (data.detail || data.message)) || `${word}失败`);
    return;
  }
  await loadTeamMembers();
}

export async function loadTeamMembers() {
  const box = $("team-members");
  if (!box) return;
  box.textContent = "加载中…";
  const [assignable, res] = await Promise.all([
    loadAssignable(),
    api("GET", "/api/v1/merchant/team/members", { merchant: true }),
  ]);
  if (!res.ok || !res.data) {
    box.innerHTML = '<p class="mc-desc">加载失败 —— 可能当前角色没有团队管理权限。</p>';
    return;
  }
  const myPid = (res.data.items || []).find((x) => x.is_self)?.person_id || null;
  renderMembers(res.data.items || [], assignable, myPid);
}

async function renderInvites() {
  const box = $("team-invite-list");
  if (!box) return;
  box.textContent = "加载中…";
  const { ok, data } = await api("GET", "/api/v1/merchant/team/invites", { merchant: true });
  if (!ok || !data) {
    box.innerHTML = '<p class="mc-desc">加载失败。</p>';
    return;
  }
  const items = data.items || [];
  if (!items.length) {
    box.innerHTML = '<p class="mc-desc">还没有邀请码。</p>';
    return;
  }
  const mayInvite = canInvite();
  box.innerHTML = items
    .map((it) => {
      const st = String(it.status || "");
      const label = INVITE_STATUS_LABEL[st] || st;
      const ops =
        st === "pending" && mayInvite
          ? `<button type="button" class="primary js-team-qr" data-id="${escHtml(it.invite_id)}">二维码</button>` +
            `<button type="button" class="secondary js-team-revoke" data-id="${escHtml(it.invite_id)}">撤销</button>`
          : "";
      return (
        `<div class="mc-team-row">` +
        `<div class="mc-team-cell"><span class="mcp-badge ${st === "pending" ? "live" : "bad"}">${escHtml(label)}</span></div>` +
        `<div class="mc-team-cell mc-desc">已用 ${Number(it.used_count || 0)}/${Number(it.max_uses || 1)}</div>` +
        `<div class="mc-team-cell mc-desc">到期 ${escHtml(fmtTime(it.expires_at))}</div>` +
        `<div class="mc-team-cell mc-team-ops">${ops}</div>` +
        `</div>`
      );
    })
    .join("");
  box.querySelectorAll(".js-team-qr").forEach((btn) => {
    btn.addEventListener("click", () => showQr(btn.getAttribute("data-id") || ""));
  });
  box.querySelectorAll(".js-team-revoke").forEach((btn) => {
    btn.addEventListener("click", () => revokeInvite(btn));
  });
}

async function createInvite() {
  const ttlSel = $("team-invite-ttl");
  const ttl = ttlSel ? Number(ttlSel.value) || 7 : 7;
  const { ok, data } = await api("POST", "/api/v1/merchant/team/invites", {
    body: { ttl_days: ttl, max_uses: 1 },
  });
  if (!ok) {
    alert((data && (data.detail || data.message)) || "生成失败");
    return;
  }
  await renderInvites();
  if (data && data.invite_id) showQr(data.invite_id);
}

async function revokeInvite(btn) {
  const id = btn.getAttribute("data-id") || "";
  if (!confirm("确认撤销这张邀请码？撤销后二维码立即失效。")) return;
  btn.disabled = true;
  const { ok, data } = await api(
    "POST",
    `/api/v1/merchant/team/invites/${encodeURIComponent(id)}/revoke`,
    {}
  );
  btn.disabled = false;
  if (!ok) {
    alert((data && (data.detail || data.message)) || "撤销失败");
    return;
  }
  await renderInvites();
}

async function showQr(inviteId) {
  const modal = $("team-qr-modal");
  const body = $("team-qr-body");
  if (!modal || !body) return;
  body.innerHTML = "正在生成二维码…";
  modal.classList.remove("hidden");
  const { ok, data } = await api(
    "POST",
    `/api/v1/merchant/team/invites/${encodeURIComponent(inviteId)}/qr`,
    {}
  );
  if (!ok || !data) {
    body.innerHTML = "二维码生成失败，请重试。";
    return;
  }
  const sess = data.session || {};
  const img = sess.qr_image_data_url || "";
  const url = sess.authorize_url || "";
  body.innerHTML =
    (img ? `<img src="${img}" alt="员工邀请二维码" style="width:220px;height:220px" />` : "") +
    `<p class="mc-desc">让员工用微信扫码加入本店。扫码时会要求他授权微信头像和昵称（<b>必须</b>，否则加入不了）；加入后没有任何权限，需要你在「成员」里分配角色。</p>` +
    `<p class="mc-desc">员工扫不出头像或昵称？<a href="/static/guide/staff-auth.html" target="_blank" rel="noopener">看指导 ›</a></p>` +
    (url
      ? `<p class="mc-desc" style="word-break:break-all">链接：<a href="${escHtml(url)}" target="_blank" rel="noopener">${escHtml(url)}</a></p>`
      : "");
}

function switchTab(name) {
  document.querySelectorAll("[data-team-tab]").forEach((b) => {
    b.classList.toggle("active", b.getAttribute("data-team-tab") === name);
  });
  const m = $("team-members");
  const i = $("team-invites");
  if (m) m.classList.toggle("hidden", name !== "members");
  if (i) i.classList.toggle("hidden", name !== "invites");
  if (name === "invites") renderInvites();
  else loadTeamMembers();
}

export function loadTeamPanel() {
  switchTab("members");
}

export function initTeamHandlers() {
  document.querySelectorAll("[data-team-tab]").forEach((b) => {
    b.addEventListener("click", () => switchTab(b.getAttribute("data-team-tab") || "members"));
  });
  const createBtn = $("btn-team-invite-new");
  if (createBtn) {
    if (!canInvite()) createBtn.classList.add("hidden");
    else createBtn.addEventListener("click", createInvite);
  }
  const closeBtn = $("btn-team-qr-close");
  if (closeBtn)
    closeBtn.addEventListener("click", () => {
      const modal = $("team-qr-modal");
      if (modal) modal.classList.add("hidden");
    });
}
